import React, { useState, useEffect } from "react";
import { adminGetServiceRequests, adminUpdateServiceRequest, adminDeleteServiceRequest } from "../../api";
import { ShieldCheck, Loader2, Trash2 } from "lucide-react";
import { useToast } from "../../hooks/use-toast";

const STATUSES = ["requested", "in_progress", "complete", "declined"];
const LABEL = { requested: "Requested", in_progress: "In progress", complete: "Complete", declined: "Declined" };
const CHIP = { requested: "bg-amber-100 text-amber-700", in_progress: "bg-sky-100 text-sky-700", complete: "bg-green-100 text-green-700", declined: "bg-gray-100 text-gray-600" };

const AdminServiceRequests = () => {
  const { toast } = useToast();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");

  const load = () => { setLoading(true); adminGetServiceRequests(filter).then(setRows).catch(() => setRows([])).finally(() => setLoading(false)); };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const setStatus = async (r, status) => {
    try { const upd = await adminUpdateServiceRequest(r.id, status, r.adminNote || ""); setRows((xs) => xs.map((x) => x.id === r.id ? upd : x)); toast({ title: `Marked ${LABEL[status]}` }); }
    catch { toast({ title: "Update failed", variant: "destructive" }); }
  };
  const saveNote = async (r, adminNote) => {
    try { await adminUpdateServiceRequest(r.id, r.status, adminNote); toast({ title: "Note saved" }); }
    catch { toast({ title: "Couldn't save note", variant: "destructive" }); }
  };
  const remove = async (r) => {
    if (!window.confirm(`Delete this ${r.serviceName} request from ${r.userName}?`)) return;
    try { await adminDeleteServiceRequest(r.id); setRows((xs) => xs.filter((x) => x.id !== r.id)); toast({ title: "Request deleted" }); }
    catch { toast({ title: "Delete failed", variant: "destructive" }); }
  };

  return (
    <div className="space-y-5" data-testid="admin-service-requests">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-[22px] font-semibold text-[#2b2b2b] flex items-center gap-2"><ShieldCheck size={20} className="text-[#b8935f]" /> Landlord Service Requests</h2>
          <p className="text-[13px] text-[#8a8a8a] mt-1">Referencing, AML/Right-to-Rent, rent guarantee, rent reconciliation and compliance-reminder requests from landlords.</p>
        </div>
        <select value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="sr-filter" className="h-9 px-3 rounded-lg border border-[#f0ece5] text-[13px] bg-white">
          <option value="">All statuses</option>
          {STATUSES.map((s) => <option key={s} value={s}>{LABEL[s]}</option>)}
        </select>
      </div>

      {loading ? <div className="py-14 grid place-items-center text-[#8a8a8a]"><Loader2 className="animate-spin" /></div>
        : rows.length === 0 ? <div className="py-14 text-center text-[#8a8a8a] bg-white border border-[#f0ece5] rounded-xl">No service requests yet.</div>
        : (
          <div className="space-y-2">
            {rows.map((r) => (
              <div key={r.id} className="bg-white border border-[#f0ece5] rounded-xl p-4" data-testid={`sr-row-${r.id}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <p className="text-[15px] font-500 text-[#2b2b2b]">{r.serviceName}</p>
                    <p className="text-[13px] text-[#6a6a6a]">{r.userName} · {r.userEmail}{r.propertyRef ? ` · ${r.propertyRef}` : ""}</p>
                    {r.notes && <p className="text-[13px] text-[#8a8a8a] mt-1">“{r.notes}”</p>}
                    <p className="text-[11px] text-[#a0a0a0] mt-1">{new Date(r.createdAt).toLocaleString("en-GB")}</p>
                  </div>
                  <span className={`text-[12px] px-2.5 py-1 rounded-full shrink-0 ${CHIP[r.status]}`}>{LABEL[r.status]}</span>
                  <button onClick={() => remove(r)} data-testid={`sr-delete-${r.id}`} title="Delete request" className="text-red-500 hover:bg-red-50 p-1.5 rounded-lg shrink-0"><Trash2 size={15} /></button>
                </div>
                <div className="flex flex-wrap items-center gap-2 mt-3">
                  {STATUSES.map((s) => (
                    <button key={s} onClick={() => setStatus(r, s)} data-testid={`sr-set-${s}-${r.id}`} className={`text-[12px] px-3 py-1.5 rounded-lg border transition-colors ${r.status === s ? "bg-[#b8935f] text-white border-[#b8935f]" : "border-[#e2d8c8] text-[#6a6a6a] hover:border-[#b8935f]"}`}>{LABEL[s]}</button>
                  ))}
                  <input defaultValue={r.adminNote} onBlur={(e) => e.target.value !== (r.adminNote || "") && saveNote(r, e.target.value)} placeholder="Add a note for the landlord…" className="flex-1 min-w-[180px] h-9 px-3 rounded-lg border border-[#e2d8c8] text-[13px] outline-none focus:border-[#b8935f]" data-testid={`sr-note-${r.id}`} />
                </div>
              </div>
            ))}
          </div>
        )}
    </div>
  );
};

export default AdminServiceRequests;
